import React, { useMemo } from 'react'
import { Box, ButtonBase, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { useLocation, useNavigate } from 'react-router-dom'
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined'
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined'
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import { BAN_ONLINE_PERMISSION_CODES as P } from '@/constants/chucNangs/banOnlineChucNangs'
import routerConstants from '@/constants/routerConstants'
import { banhangKiotLike } from '@/components/layout/banhangKiotTokens'
import { useI18n } from '@/contexts/I18nContext'
import { useMenuChucNang } from '@/hooks/useMenuChucNang'

type Muc = {
  to: string
  labelKey: string
  perm: string
  icon: React.ReactNode
  /** Path phụ vẫn highlight mục này (vd. thiết lập kênh nằm trong gian hàng). */
  activePaths?: string[]
}

const MUC: Muc[] = [
  {
    to: routerConstants.donHangOnline.list,
    labelKey: 'components.menu.donHangOnlineList',
    perm: P.WEB_BH_DON_HANG_ONLINE_LIST,
    icon: <ShoppingBagOutlinedIcon />,
  },
  {
    to: routerConstants.hangHoaLienKet.list,
    labelKey: 'components.menu.hangHoaLienKetList',
    perm: P.WEB_BH_HANG_HOA_LIEN_KET_LIST,
    icon: <InventoryOutlinedIcon />,
  },
  {
    to: routerConstants.doiSoatOnline.list,
    labelKey: 'components.menu.doiSoatOnlineList',
    perm: P.WEB_BH_DOI_SOAT_ONLINE_LIST,
    icon: <PaidOutlinedIcon />,
  },
  {
    to: routerConstants.gianHangOnline.list,
    labelKey: 'components.menu.gianHangOnlineList',
    perm: P.WEB_BH_GIAN_HANG_ONLINE_LIST,
    icon: <StorefrontOutlinedIcon />,
    // Thiết lập kênh mở bằng nút trong trang gian hàng — không còn menu riêng.
    activePaths: [routerConstants.thietLapKenhOnline],
  },
]

/** Chiều cao cố định khối menu — tách biệt khối lọc bên dưới. */
const MENU_HEIGHT_PX = 196

const pathGoc = (to: string) => to.replace(/\/list$/, '')

const laActive = (pathname: string, to: string, activePaths?: string[]) => {
  if (pathname === to) return true
  if (activePaths?.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return true
  if (to.endsWith('/list')) return pathname.startsWith(`${pathGoc(to)}/`)
  return false
}

/** Landing khi bấm TopNav «Bán online» — trang đầu user có quyền. */
export function resolveBanOnlineLanding(hasChucNangByCode: (ma: string) => boolean): string {
  for (const m of MUC) {
    if (hasChucNangByCode(m.perm)) return m.to
  }
  return routerConstants.donHangOnline.list
}

export const BAN_ONLINE_TOP_PERMS = MUC.map((m) => m.perm)

/**
 * Menu con module Bán online — chiều cao cố định, chữ to, khác style khối lọc.
 */
export const BanOnlineModuleNav: React.FC = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { t } = useI18n()
  const { hasChucNangByCode } = useMenuChucNang()

  const items = useMemo(
    () => MUC.filter((m) => hasChucNangByCode(m.perm)),
    [hasChucNangByCode],
  )

  if (items.length === 0) return null

  return (
    <Box
      component="nav"
      aria-label="Bán online"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: MENU_HEIGHT_PX,
        minHeight: MENU_HEIGHT_PX,
        maxHeight: MENU_HEIGHT_PX,
        flexShrink: 0,
        overflow: 'hidden',
        boxSizing: 'border-box',
        borderRadius: '10px',
        bgcolor: '#eef2f6',
        border: '1px solid #e0e5eb',
      }}
    >
      {items.map((m) => {
        const active = laActive(pathname, m.to, m.activePaths)
        return (
          <ButtonBase
            key={m.to}
            onClick={() => {
              if (!active) navigate(m.to)
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 1.25,
              width: '100%',
              flex: 1,
              minHeight: 0,
              px: 1.5,
              borderRadius: 0,
              textAlign: 'left',
              fontFamily: banhangKiotLike.kiotFontStack,
              color: active ? 'primary.main' : banhangKiotLike.kiotTextPrimary,
              bgcolor: active ? '#ffffff' : 'transparent',
              fontWeight: active ? 700 : 600,
              borderLeft: '3px solid',
              borderLeftColor: active ? 'primary.main' : 'transparent',
              boxShadow: active ? 'inset 0 -1px 0 #e8edf2, inset 0 1px 0 #e8edf2' : 'none',
              transition: 'background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease',
              '&:hover': {
                bgcolor: active ? '#ffffff' : alpha(theme.palette.common.black, 0.04),
              },
              '& svg': {
                fontSize: 22,
                flexShrink: 0,
                opacity: active ? 1 : 0.8,
              },
            }}
          >
            {m.icon}
            <Typography
              component="span"
              sx={{
                fontSize: 15,
                fontWeight: 'inherit',
                lineHeight: 1.3,
                letterSpacing: 0,
                color: 'inherit',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {t(m.labelKey)}
            </Typography>
          </ButtonBase>
        )
      })}
    </Box>
  )
}
